import React, {
  useEffect,
} from 'react';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import {
  SendPatientPCCConsentRequestDTO,
} from 'dtos';
import {
  useNotify,
} from 'core/hooks';
import Controller from 'core/components/FormController';
import PhoneInput from 'core/components/PhoneInput';
import {
  Button,
} from '@populateui/ui';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import Dialog from 'pages/Dashboard/components/Dialog';
import Input from 'pages/Dashboard/components/Input';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  useSendPCCConsent,
} from 'views/EMR/hooks/pcc';
import {
  patientAlertsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useGetPhoneNumberTag,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  normalizePhoneNumber,
  validateEmailIfSet,
  validatePhoneIfSet,
} from 'pages/Dashboard/utils/helper';

type Props = {
  patientId: number;
  patientEmail?: string | null;
  patientPhone?: string | null;
  onClose: () => void;
};

type SendConsentFormValues = {
  emailSelected: boolean;
  email: string;
  phoneSelected: boolean;
  phone: string;
};

const defaultValues: SendConsentFormValues = {
  emailSelected: false,
  email: '',
  phoneSelected: false,
  phone: '',
};

export default function SendConsentFormDialog({
  patientId,
  patientEmail,
  patientPhone,
  onClose,
}: Props) {
  const notify = useNotify();
  const { mutateAsync: sendPCCConsent } = useSendPCCConsent();

  const formProps = useForm<SendConsentFormValues>({
    mode: 'all',
    defaultValues,
  });
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
    reset,
    watch,
  } = formProps;
  const [
    emailSelected,
    phoneSelected,
    phone,
  ] = watch([
    'emailSelected',
    'phoneSelected',
    'phone',
  ]);

  const {
    isLoading: isLoadingPhoneNumberDetails,
    phoneTag,
  } = useGetPhoneNumberTag(phone);

  const disabled = (
    (!emailSelected && !phoneSelected)
    || !isValid
    || isSubmitting
    || (phoneSelected && isLoadingPhoneNumberDetails)
  );

  async function onSubmit({
    emailSelected,
    phoneSelected,
    email,
    phone,
  }: SendConsentFormValues) {
    try {
      const payload: SendPatientPCCConsentRequestDTO = {
        patientId,
        email: emailSelected ? email : null,
        phoneNumber: phoneSelected ? normalizePhoneNumber(phone) : null,
      };
      await sendPCCConsent(payload);
      notify({
        variant: 'success',
        message: 'Consent form sent successfully',
      });
      patientAlertsEventChannel.emit('onPatientAlertsChanged', patientId);
      onClose();
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    reset({
      emailSelected: Boolean(patientEmail),
      email: patientEmail ?? '',
      phoneSelected: Boolean(patientPhone),
      phone: patientPhone ?? '',
    });
  }, [
    reset,
    patientEmail,
    patientPhone,
  ]);

  return (
    <Dialog
      open
      title="Send Consent Form"
      onClose={onClose}
      onConfirm={() => { }}
      closeBtnClasses="!top-0"
      confirmText="Create"
      dialogClassName="rounded-lg"
      maxWidth="md"
      disableEscapeKeyDown
      disableBackdropClick
      className="nw-send-consent-form w-max"
    >
      <FormProvider {...formProps}>
        <div className="mt-5 flex flex-col gap-form-row mb-8 patient-forms">
          <h2 className="font-bold text-sm">
            Send via
          </h2>
          <div className="flex flex-row gap-form-field items-center">
            <Controller
              control={control}
              name="emailSelected"
              render={({ field }) => (
                <Checkbox
                  label="Email"
                  id="send-consent-via-email"
                  onChange={field.onChange}
                  checked={field.value}
                />
              )}
            />
            <Controller
              control={control}
              name="email"
              rules={{ required: emailSelected, validate: validateEmailIfSet }}
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  {...field}
                  error={invalid}
                  showErrorText={false}
                  id="send-consent-email"
                  className="text-gray-900 w-full text-xs"
                />
              )}
            />
          </div>
          <div className="flex flex-row gap-form-field items-center">
            <Controller
              control={control}
              name="phoneSelected"
              render={({ field }) => (
                <Checkbox
                  label="Text"
                  id="send-consent-via-phone"
                  onChange={field.onChange}
                  checked={field.value}
                />
              )}
            />
            <Controller
              control={control}
              name="phone"
              rules={{ required: phoneSelected, validate: validatePhoneIfSet }}
              render={({ field }) => (
                <Wrapper
                  id="send-consent-phone-number"
                  className="w-full"
                >
                  <PhoneInput
                    {...field}
                    isLoadingPhoneTag={isLoadingPhoneNumberDetails}
                    phoneTag={phoneTag}
                    id="send-consent-phone-number"
                    name="send-consent-phone-number"
                  />
                </Wrapper>
              )}
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 m-3 mr-0">
          <Button
            className="nw-confirm !py-1.5 ml-3"
            variant="contained"
            type="button"
            color="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={disabled}
          >
            Send
          </Button>
        </div>
      </FormProvider>
    </Dialog>
  );
}
